import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getPartnerFaviconUrl } from './PartnerFavicon';

const SectionWrapper = styled.section`
    position: relative;
    width: 100%;
    background: #00573f;
    overflow: visible;
`;

const TextureLayer = styled.div`
    position: absolute;
    inset: 0;
    background-image: ${({ textureImage }) => (textureImage ? `url(${textureImage})` : 'none')};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    mix-blend-mode: multiply;
    opacity: 0.5;
    pointer-events: none;
`;

const SectionInner = styled.div`
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    min-height: 525px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 72px 24px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        min-height: auto;
        padding: 40px 16px 36px;
    }
`;

const ContentBox = styled.div`
    display: flex;
    width: 846px;
    max-width: 100%;
    flex-direction: column;
    align-items: center;
    gap: 40px;
    text-align: center;

    @media (max-width: 768px) {
        width: 100%;
        gap: 28px;
    }
`;

const LogoBadge = styled.div`
    width: 100px;
    height: 100px;
    border-radius: 100px;
    border: 3px solid #ddedd0;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    box-sizing: border-box;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);

    img {
        width: 73px;
        height: auto;
        display: block;
        object-fit: contain;
    }

    @media (max-width: 768px) {
        width: 92px;
        height: 92px;
        border-width: 2px;

        img {
            width: 58px;
        }
    }
`;

const TextBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    max-width: 720px;

    @media (max-width: 768px) {
        gap: 10px;
        max-width: 100%;
    }
`;

const Heading = styled.h2`
    margin: 0;
    color: #fff;
    text-align: center;
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    font-style: normal;
    font-weight: 500;
    line-height: 42px;

    @media (max-width: 768px) {
        font-size: 24px;
        line-height: 34px;
    }
`;

const Description = styled.p`
    margin: 0;
    color: #fff;
    text-align: center;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 180%;
    max-width: 680px;

    @media (max-width: 768px) {
        font-size: 14px;
        line-height: 180%;
        max-width: 100%;
    }
`;

const ShareButton = styled.button`
    display: flex;
    padding: 8px 39px;
    justify-content: center;
    align-items: flex-start;
    gap: 10px;
    border: none;
    border-radius: 30px;
    background: #dd6b17;
    color: #fff;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 28.8px;
    cursor: pointer;
    transition: transform 0.2s ease, opacity 0.2s ease;

    &:hover {
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }

    @media (max-width: 768px) {
        padding: 8px 30px;
        font-size: 15px;
        line-height: 26px;
    }
`;

const LeafDecor = styled.img`
    position: absolute;
    right: 0px;
    top: -80px;
    width: 176px;
    height: auto;
    z-index: 1;
    pointer-events: none;
    user-select: none;

    @media (max-width: 1024px) {
        right: 0px;
        top: -70px;
        width: ${({ small }) => (small ? '118px' : '190px')};
    }

    @media (max-width: 768px) {
        right: 0px;
        top: -50px;
        width: ${({ small }) => (small ? '82px' : '138px')};
    }
`;
const BADGE_ICON_MIN_PX = 46;
const BADGE_ICON_MAX_PX = 72;
const BADGE_ICON_DEFAULT_PX = 60;

const SharePageSection = ({
    partnerName = 'Explorandes',
    heading = 'Share the good work of [Partner name]!',
    description = 'Cool what they are doing, right? Spread the word and share this page in your network.',
    partnerLogo,
    partnerSlug, // NEW: used to resolve the favicon (same as the modal)
    partnerWebsite, // OPTIONAL: org website/domain if you store one (overrides the map)
    textureImage,
    bigLeaf,
    smallLeaf,
    onShare,
}) => {
    const resolvedHeading = heading.replace('[Partner name]', partnerName);

    const faviconUrl = getPartnerFaviconUrl(partnerSlug, partnerWebsite);

    const [iconNaturalSize, setIconNaturalSize] = useState(null);
    const [iconFailed, setIconFailed] = useState(false);

    useEffect(() => {
        setIconNaturalSize(null);
        setIconFailed(false);
    }, [faviconUrl]);

    const handleIconLoad = e => {
        const natural = Math.max(e.target.naturalWidth || 0, e.target.naturalHeight || 0);
        setIconNaturalSize(natural);
    };

    const renderedIconSize = iconNaturalSize
        ? Math.min(Math.max(iconNaturalSize, BADGE_ICON_MIN_PX), BADGE_ICON_MAX_PX)
        : BADGE_ICON_DEFAULT_PX;

    const showFavicon = Boolean(faviconUrl) && !iconFailed;

    const handleShare = async () => {
        if (onShare) {
            onShare();
            return;
        }

        const shareData = {
            title: resolvedHeading,
            text: description,
            url: window.location.href,
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
                return;
            } catch (err) {}
        }

        try {
            await navigator.clipboard.writeText(window.location.href);
        } catch (err) {}
    };

    return (
        <SectionWrapper>
            <TextureLayer textureImage={textureImage} />

            {smallLeaf ? <LeafDecor src={smallLeaf} alt="" small /> : null}

            <SectionInner>
                <ContentBox>
                    <LogoBadge>
                        {showFavicon ? (
                            <img
                                src={faviconUrl}
                                alt={`${partnerName} logo`}
                                onLoad={handleIconLoad}
                                onError={() => setIconFailed(true)}
                                style={{
                                    width: '75px',
                                    height: '75px',
                                    objectFit: 'contain',
                                    display: 'block',
                                    opacity: iconNaturalSize ? 1 : 0,
                                    transition: 'opacity 160ms ease',
                                }}
                            />
                        ) : (
                            <img src={partnerLogo} alt={`${partnerName} logo`} />
                        )}
                    </LogoBadge>

                    <TextBlock>
                        <Heading>{resolvedHeading}</Heading>
                        <Description>{description}</Description>
                    </TextBlock>

                    <ShareButton type="button" onClick={handleShare}>
                        Share this page
                    </ShareButton>
                </ContentBox>
            </SectionInner>
        </SectionWrapper>
    );
};

export default SharePageSection;
